import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle as SvgCircle } from 'react-native-svg';
import * as DocumentPicker from 'expo-document-picker';
import { FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { kpisApi, kpiValuesApi, schedulesApi, evidenceApi } from '../../services/apiService';
import { KPI, KPIValue, KPIReadingScheduleList } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useTranslation } from 'react-i18next';
import { normalizeArabicNumbers } from '../../utils/rtl';

type RouteProps = RouteProp<MainStackParamList, 'AddKPIReading'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const InfoIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5"/>
    <Path d="M12 16V12M12 8H12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </Svg>
);

const EyeIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Progress Circle Component
const ProgressCircle = ({ percentage, readings, total }: { percentage: number; readings: number; total: number }) => {
  const size = 64;
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(readings / total, 1);
  const strokeDashoffset = circumference - progress * circumference;

  return (
    <View style={progressStyles.container}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#22C55E"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
    </View>
  );
};

const progressStyles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// ============================================================
// Helpers
// ============================================================

const getExpectedReadingsPerYear = (frequency: string | undefined): number => {
  const frequencyMap: Record<string, number> = {
    daily: 365,
    weekly: 52,
    monthly: 12,
    quarterly: 4,
    half_yearly: 2,
    yearly: 1,
  };
  return frequencyMap[frequency || ''] || 0;
};

/**
 * Get the start and end dates of the period containing `date` based on KPI frequency.
 * Used to check if a reading already exists for the same period.
 */
const getPeriodBoundaries = (dateStr: string, frequency: string): { start: string; end: string } => {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed

  // Use local date parts to avoid UTC timezone shift (toISOString shifts dates in UTC+ zones)
  const fmt = (dt: Date) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  switch (frequency) {
    case 'daily':
      return { start: dateStr, end: dateStr };
    case 'weekly': {
      const day = d.getDay(); // 0=Sun
      const monday = new Date(d);
      monday.setDate(d.getDate() - ((day + 6) % 7)); // Go back to Monday
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: fmt(monday), end: fmt(sunday) };
    }
    case 'monthly': {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0); // Last day of month
      return { start: fmt(start), end: fmt(end) };
    }
    case 'quarterly': {
      const q = Math.floor(month / 3);
      const start = new Date(year, q * 3, 1);
      const end = new Date(year, q * 3 + 3, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'half_yearly': {
      if (month < 6) {
        return { start: `${year}-01-01`, end: `${year}-06-30` };
      }
      return { start: `${year}-07-01`, end: `${year}-12-31` };
    }
    case 'yearly':
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    default:
      return { start: dateStr, end: dateStr };
  }
};

/**
 * Returns the window during which a reading can be entered for a scheduled period.
 * For longer frequencies, the window is the last month of the period.
 * - daily/weekly/monthly: same as period boundaries (full period)
 * - quarterly: last month of the quarter
 * - half_yearly: last month of the half
 * - yearly: last month of the year (December)
 */
const getReadingWindow = (dateStr: string, frequency: string): { start: string; end: string } => {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed

  const fmt = (dt: Date) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // For quarterly, half_yearly, and yearly: the reading window is the
  // month of the scheduled_date (supports both calendar and academic year schedules).
  switch (frequency) {
    case 'quarterly':
    case 'half_yearly':
    case 'yearly': {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    default:
      return getPeriodBoundaries(dateStr, frequency);
  }
};

const hasReadingInPeriod = (readings: KPIValue[], dateStr: string, frequency: string): boolean => {
  if (!dateStr || !frequency) return false;
  const { start, end } = getPeriodBoundaries(dateStr, frequency);
  return readings.some(r => r.measurement_date >= start && r.measurement_date <= end);
};

/**
 * Check whether a chosen manual-entry date falls inside the allowed reading window
 * for the KPI's frequency. For quarterly/half_yearly/yearly, the window is the last
 * month of the period (e.g., yearly = December). For daily/weekly/monthly the
 * window equals the full period, so this always returns true.
 */
const isDateWithinReadingWindow = (dateStr: string, frequency: string): boolean => {
  if (!dateStr || !frequency) return true;
  const { start, end } = getReadingWindow(dateStr, frequency);
  return dateStr >= start && dateStr <= end;
};

const getPeriodLabel = (frequency: string): string => {
  const labels: Record<string, string> = {
    daily: 'اليوم',
    weekly: 'هذا الأسبوع',
    monthly: 'هذا الشهر',
    quarterly: 'هذا الربع',
    half_yearly: 'هذا النصف من السنة',
    yearly: 'هذه السنة',
  };
  return labels[frequency] || 'هذه الفترة';
};

// ============================================================
// Main Component
// ============================================================

export default function AddKPIReadingScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { kpiId, readingId } = route.params;
  const isEditMode = !!readingId;
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { translate, translateOrg } = useAutoTranslate();
  const { navigateToHome } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, width, containerPadding, cardPadding, sectionSpacing } = useResponsive();

  // Theme colors for form elements
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F3F4F6',
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    infoMessage: isDark ? colors.backgroundAlt : '#F1F5F9',
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    radioBorder: isDark ? '#4B5563' : '#D1D5DB',
    readingBg: isDark ? colors.backgroundAlt : '#F9FAFB',
    badgeBg: isDark ? 'rgba(100, 116, 139, 0.2)' : 'rgba(51, 65, 85, 0.1)',
  };

  // Reading entry is governed by the KPI's authoritative `can_enter_reading`
  // flag (API-computed: implementing_section for an independent strategic KPI,
  // owning section head / creator / assigned employee for operational ones).
  // The old gate blocked anyone holding strategic_goals.create — which wrongly
  // bounced a Section Head who IS the implementing section / KPI owner.
  // We bounce only once the KPI has loaded and the API says they can't enter.

  // Responsive breakpoints
  const isSmallScreen = isMobile;

  const [kpi, setKpi] = useState<KPI | null>(null);
  const [recentReadings, setRecentReadings] = useState<KPIValue[]>([]);
  const [allReadings, setAllReadings] = useState<KPIValue[]>([]);
  const [currentYearReadingsCount, setCurrentYearReadingsCount] = useState(0);
  const [lateSchedules, setLateSchedules] = useState<KPIReadingScheduleList[]>([]);
  const [pendingSchedules, setPendingSchedules] = useState<KPIReadingScheduleList[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state
  const [selectedPeriod, setSelectedPeriod] = useState<'late' | 'scheduled' | 'manual'>('scheduled');
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [evidenceType, setEvidenceType] = useState<'file' | 'text'>('file');
  const [selectedFile, setSelectedFile] = useState<DocumentPicker.DocumentPickerAsset | null>(null);
  const [formData, setFormData] = useState({
    value: '',
    measurementDate: new Date().toISOString().split('T')[0],
    notes: '',
    evidenceText: '',
  });

  useUnsavedChangesWarning(!!formData.value || !!formData.notes);

  // File picker handler
  const handleFilePick = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/zip', 'application/x-zip-compressed'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        setSelectedFile(result.assets[0]);
        if (errors.evidence) {
          setErrors(prev => ({ ...prev, evidence: '' }));
        }
      }
    } catch (error) {
      console.error('Error picking file:', error);
      showError(t('readings.filePickFailed'));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      await fetchData();
    };
    load();
    return () => { cancelled = true; };
  }, [kpiId, readingId]);

  // Once the KPI loads, bounce the user only if the API says they cannot enter
  // a reading for it (aggregate KPI, or not the implementing section / owner).
  useEffect(() => {
    if (kpi && (kpi as any).can_enter_reading === false) {
      showError(t('readings.noPermissionToAddReadings'));
      navigation.goBack();
    }
  }, [kpi, navigation, showError, t]);

  const fetchData = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const promises: Promise<any>[] = [
        kpisApi.get(kpiId),
        kpisApi.getReadings(kpiId, { limit: 3 }),
        kpisApi.getSchedules(kpiId),
        kpisApi.getReadings(kpiId, { start_date: `${currentYear - 1}-01-01`, end_date: `${currentYear}-12-31` }),
      ];
      if (isEditMode) {
        promises.push(kpiValuesApi.get(readingId));
      }
      let [kpiData, readingsData, schedulesData, yearReadingsData, existingReading] = await Promise.all(promises);
      setKpi(kpiData);
      setRecentReadings(Array.isArray(readingsData) ? readingsData.slice(0, 3) : []);
      setAllReadings(Array.isArray(yearReadingsData) ? yearReadingsData : []);
      setCurrentYearReadingsCount(Array.isArray(yearReadingsData) ? yearReadingsData.length : 0);

      // Auto-generate schedules if none exist
      if (!Array.isArray(schedulesData) || schedulesData.length === 0) {
        try {
          await schedulesApi.generate(kpiId, new Date().getFullYear());
          schedulesData = await kpisApi.getSchedules(kpiId);
        } catch (e) {
          console.warn('Failed to auto-generate schedules:', e);
        }
      }

      // Pre-fill form in edit mode
      if (isEditMode && existingReading) {
        setFormData({
          value: String(existingReading.value || ''),
          measurementDate: existingReading.measurement_date || new Date().toISOString().split('T')[0],
          notes: existingReading.notes || '',
          evidenceText: '',
        });
        setSelectedPeriod('manual');
      }

      // Separate schedules into late and current pending (only current period)
      const yearReadings: KPIValue[] = Array.isArray(yearReadingsData) ? yearReadingsData : [];
      const freq = kpiData.frequency || '';
      const todayStr = new Date().toISOString().split('T')[0];

      if (Array.isArray(schedulesData)) {
        const now = new Date();

        // Filter out schedules whose period already has a reading
        const hasNoReadingForPeriod = (s: KPIReadingScheduleList) =>
          !hasReadingInPeriod(yearReadings, s.scheduled_date, freq);

        const late = schedulesData.filter((s: KPIReadingScheduleList) => {
          if (s.is_completed) return false;
          const { end } = getReadingWindow(s.scheduled_date, freq);
          return todayStr > end;
        }).filter(hasNoReadingForPeriod);

        // Only show pending schedules whose period includes today
        // (e.g., for weekly: only show if today falls within that week)
        const currentPeriodSchedules = schedulesData.filter((s: KPIReadingScheduleList) => {
          if (s.is_completed) return false;
          const { start, end } = getReadingWindow(s.scheduled_date, freq);
          return todayStr >= start && todayStr <= end;
        }).filter(hasNoReadingForPeriod);

        const pending = currentPeriodSchedules.length > 0 ? [currentPeriodSchedules[0]] : [];
        setLateSchedules(late);
        setPendingSchedules(pending);

        // Auto-select first pending schedule if available
        if (pending.length > 0) {
          setSelectedScheduleId(pending[0].id);
          setSelectedPeriod('scheduled');
        } else if (late.length > 0) {
          setSelectedScheduleId(late[0].id);
          setSelectedPeriod('late');
        } else {
          setSelectedPeriod('manual');
        }
      }
    } catch (err: any) {
      showError(t('kpis.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const isLateReading = selectedPeriod === 'late';

  // Check if a reading already exists for the selected period (manual mode)
  const periodAlreadyHasReading = !isEditMode && selectedPeriod === 'manual' && kpi
    ? hasReadingInPeriod(allReadings, formData.measurementDate, kpi.frequency)
    : false;

  // Check if manual-entry date is outside the KPI's allowed reading window
  // (e.g., yearly KPIs only accept December dates, quarterly only the last month)
  const isDateOutsideDueWindow = !isEditMode && selectedPeriod === 'manual' && kpi && formData.measurementDate
    ? !isDateWithinReadingWindow(formData.measurementDate, kpi.frequency)
    : false;

  const dueWindowRange = !isEditMode && selectedPeriod === 'manual' && kpi && formData.measurementDate
    ? getReadingWindow(formData.measurementDate, kpi.frequency)
    : null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.value.trim()) {
      newErrors.value = t('validation.valueRequired');
    } else if (isNaN(parseFloat(formData.value))) {
      newErrors.value = t('validation.valueMustBeNumber');
    }

    if (selectedPeriod === 'manual' && !formData.measurementDate) {
      newErrors.measurementDate = t('validation.measurementDateRequired');
    } else if (periodAlreadyHasReading) {
      const label = getPeriodLabel(kpi?.frequency || '');
      newErrors.measurementDate = `توجد قراءة مسجلة بالفعل في ${label}`;
    } else if (isDateOutsideDueWindow && dueWindowRange) {
      newErrors.measurementDate = `يجب أن يكون تاريخ القراءة ضمن فترة الاستحقاق (${formatShortDate(dueWindowRange.start)} - ${formatShortDate(dueWindowRange.end)})`;
    }

    if (isLateReading && !formData.notes.trim()) {
      newErrors.notes = t('validation.notesRequiredForLate');
    }

    // Evidence is required - either file or text
    if (evidenceType === 'file' && !selectedFile) {
      newErrors.evidence = 'يجب إرفاق ملف دليل أو إدخال نص';
    } else if (evidenceType === 'text' && !formData.evidenceText.trim()) {
      newErrors.evidence = 'يجب إدخال نص الدليل أو إرفاق ملف';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      const numericValue = parseFloat(formData.value);
      let savedReadingId: number | null = null;

      if (isEditMode && readingId) {
        // Edit mode - update existing reading
        await kpiValuesApi.update(readingId, {
          value: numericValue,
          measurement_date: formData.measurementDate,
          notes: formData.notes?.trim() || undefined,
        } as any);
        savedReadingId = readingId;
      } else if (selectedPeriod === 'manual') {
        // Manual entry - use addReading API
        const reading = await kpisApi.addReading(kpiId, {
          value: formData.value,
          measurement_date: formData.measurementDate,
          notes: formData.notes?.trim() || undefined,
        });
        savedReadingId = reading.id;
      } else if (selectedScheduleId) {
        // Scheduled or late period - use markComplete API with notes
        const result = await schedulesApi.markComplete(
          selectedScheduleId,
          numericValue,
          formData.notes?.trim() || undefined
        );
        // The markComplete might return the reading data
        if (result && result.reading_id) {
          savedReadingId = result.reading_id;
        }
      } else {
        // Fallback to addReading if no schedule selected
        const reading = await kpisApi.addReading(kpiId, {
          value: formData.value,
          measurement_date: formData.measurementDate,
          notes: formData.notes?.trim() || undefined,
        });
        savedReadingId = reading.id;
      }

      // Upload evidence (required - file or text)
      if (savedReadingId) {
        try {
          if (evidenceType === 'file' && selectedFile) {
            const formDataEvidence = new FormData();
            formDataEvidence.append('related_type', 'kpi_value');
            formDataEvidence.append('related_id', String(savedReadingId));
            formDataEvidence.append('evidence_type', 'file');
            formDataEvidence.append('title', selectedFile.name || t('readings.readingEvidence'));

            // For web, create a blob from the file URI
            if (Platform.OS === 'web') {
              const response = await fetch(selectedFile.uri);
              const blob = await response.blob();
              formDataEvidence.append('file', blob, selectedFile.name);
            } else {
              formDataEvidence.append('file', {
                uri: selectedFile.uri,
                type: selectedFile.mimeType || 'application/octet-stream',
                name: selectedFile.name || 'file',
              } as any);
            }

            await evidenceApi.create(formDataEvidence);
          } else if (evidenceType === 'text' && formData.evidenceText.trim()) {
            const formDataEvidence = new FormData();
            formDataEvidence.append('related_type', 'kpi_value');
            formDataEvidence.append('related_id', String(savedReadingId));
            formDataEvidence.append('evidence_type', 'text');
            formDataEvidence.append('title', t('readings.evidenceNotes'));
            formDataEvidence.append('text_content', formData.evidenceText);

            await evidenceApi.create(formDataEvidence);
          }
        } catch (evidenceError: any) {
          console.error('Error uploading evidence:', evidenceError);
          const evidenceMsg = evidenceError.response?.data?.error
            || evidenceError.response?.data?.detail
            || 'فشل رفع الدليل. تم حفظ القراءة بنجاح.';
          showError(evidenceMsg);
          queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
          navigation.goBack();
          return;
        }
      }

      showSuccess(isEditMode ? t('readings.editSuccess') : t('readings.addSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
      navigation.goBack();
    } catch (error: any) {
      console.error('Error adding reading:', error);
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.value?.[0]
        || error.response?.data?.measurement_date?.[0]
        || error.response?.data?.non_field_errors?.[0]
        || t('readings.addFailed');
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear evidence error when typing evidence text
    if (field === 'evidenceText' && errors.evidence) {
      setErrors(prev => ({ ...prev, evidence: '' }));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('ar-SA', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month}، ${year}`;
  };

  if (loading) {
    return (
      <PageContainer>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#A9211F" />
        </View>
      </PageContainer>
    );
  }

  const getPolarityText = (polarity: string) => {
    switch (polarity) {
      case 'positive':
        return t('readings.polarityPositive');
      case 'negative':
        return t('readings.polarityNegative');
      default:
        return polarity;
    }
  };

  // Reading item colors - muted gray-blue to match Figma
  const getReadingColor = (_index: number) => {
    return '#94A3B8';
  };

  return (
    <PageContainer>
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, { padding: containerPadding }]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('nav.home') },
            { label: t('kpis.kpiDetails'), route: 'KPIDetail', params: { id: kpiId } },
            { label: isEditMode ? t('readings.editReading') : t('readings.addReading') },
          ]}
        />

        {/* Page Title - KPI Name */}
        <View style={[styles.pageTitleRow, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          {/* <TouchableOpacity style={styles.viewAllButton}>
            <EyeIcon size={16} color="#6B7280" />
            <Text style={styles.viewAllText}>{t('readings.viewAllReadings')}</Text>
          </TouchableOpacity> */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{translate(kpi?.name) || t('kpis.addNewReading')}</Text>
        </View>

        {/* Main Content - Two Column Layout */}
        <View style={[styles.mainContent, isSmallScreen && styles.mainContentSmall]}>
          {/* Left Column - Form */}
          <View style={[styles.leftColumn, isSmallScreen && styles.leftColumnSmall]}>
            <View style={[styles.formCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.formTitle, { color: themeColors.pageTitle }]}>{t('readings.enterReadingValue')}</Text>

              {/* Periods Section - Gray Background (hidden in edit mode) */}
              {!isEditMode && (
              <View style={[styles.periodsSection, { backgroundColor: themeColors.section }]}>
                {/* Late Periods */}
                {lateSchedules.length > 0 && (
                  <View style={styles.periodGroup}>
                    <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('readings.latePeriods')}</Text>
                    {lateSchedules.map((schedule) => {
                      const isSelected = selectedPeriod === 'late' && selectedScheduleId === schedule.id;
                      return (
                        <View key={schedule.id}>
                          <TouchableOpacity
                            style={styles.periodRadioRow}
                            onPress={() => {
                              setSelectedPeriod('late');
                              setSelectedScheduleId(schedule.id);
                            }}
                          >
                            <View style={styles.periodLabelContainer}>
                              <Text style={[styles.periodText, { color: themeColors.iconColor }, isSelected && styles.periodTextActive]}>
                                {translate(schedule.period_label)}
                              </Text>
                              <Text style={[styles.dueDateText, { color: '#EF4444' }]}>
                                {t('readings.dueDate')}: {formatShortDate(schedule.scheduled_date)}
                              </Text>
                            </View>
                            <View style={[styles.radio, { borderColor: themeColors.radioBorder }, isSelected && styles.radioActive]}>
                              {isSelected && <View style={styles.radioInner} />}
                            </View>
                          </TouchableOpacity>
                        </View>
                      );
                    })}
                  </View>
                )}

                {/* Scheduled Periods */}
                {pendingSchedules.length > 0 && (
                  <View style={styles.periodGroup}>
                    <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('readings.scheduledPeriods')}</Text>
                    {pendingSchedules.map((schedule) => (
                      <TouchableOpacity
                        key={schedule.id}
                        style={styles.periodRadioRow}
                        onPress={() => {
                          setSelectedPeriod('scheduled');
                          setSelectedScheduleId(schedule.id);
                        }}
                      >
                        <Text style={[styles.periodText, { color: themeColors.iconColor }, selectedPeriod === 'scheduled' && selectedScheduleId === schedule.id && styles.periodTextActive]}>
                          {translate(schedule.period_label)}
                        </Text>
                        <View style={[styles.radio, { borderColor: themeColors.radioBorder }, selectedPeriod === 'scheduled' && selectedScheduleId === schedule.id && styles.radioActive]}>
                          {selectedPeriod === 'scheduled' && selectedScheduleId === schedule.id && <View style={styles.radioInner} />}
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}

                {/* Manual Entry Section */}
                <View style={styles.periodGroup}>
                  <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('readings.manualEntry')}</Text>
                  <TouchableOpacity
                    style={styles.periodRadioRow}
                    onPress={() => {
                      setSelectedPeriod('manual');
                      setSelectedScheduleId(null);
                    }}
                  >
                    <Text style={[styles.periodText, { color: themeColors.iconColor }, selectedPeriod === 'manual' && styles.periodTextActive]}>{t('readings.addReadingForSpecificDate')}</Text>
                    <View style={[styles.radio, { borderColor: themeColors.radioBorder }, selectedPeriod === 'manual' && styles.radioActive]}>
                      {selectedPeriod === 'manual' && <View style={styles.radioInner} />}
                    </View>
                  </TouchableOpacity>

                  {/* Date Input - shown when manual is selected */}
                  {selectedPeriod === 'manual' && (
                    <View style={styles.dateInputWrapper}>
                      <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: (periodAlreadyHasReading || isDateOutsideDueWindow) ? '#EF4444' : themeColors.inputBorder }]}>
                        <CalendarIcon size={18} color={themeColors.iconColor} />
                        {Platform.OS === 'web' ? (
                          <input
                            type="date"
                            value={formData.measurementDate}
                            min={dueWindowRange?.start}
                            max={dueWindowRange?.end}
                            onChange={(e: any) => updateField('measurementDate', e.target.value)}
                            style={{
                              flex: 1,
                              border: 'none',
                              outline: 'none',
                              backgroundColor: 'transparent',
                              fontSize: 14,
                              fontFamily: FONTS.regular,
                              color: isDark ? '#E5E7EB' : '#414042',
                              textAlign: 'right' as const,
                              direction: 'ltr' as const,
                              padding: '8px 0',
                              cursor: 'pointer',
                            }}
                          />
                        ) : (
                          <TextInput
                            style={[styles.dateInput, { color: themeColors.inputText }]}
                            value={formData.measurementDate}
                            onChangeText={(v) => updateField('measurementDate', v)}
                            placeholder="YYYY-MM-DD"
                            placeholderTextColor={themeColors.placeholder}
                            textAlign="right"
                          />
                        )}
                      </View>
                      {periodAlreadyHasReading && (
                        <View style={styles.periodWarning}>
                          <InfoIcon size={14} color="#EF4444" />
                          <Text style={styles.periodWarningText}>
                            توجد قراءة مسجلة بالفعل في {getPeriodLabel(kpi?.frequency || '')}. دورية المؤشر تسمح بقراءة واحدة فقط لكل فترة.
                          </Text>
                        </View>
                      )}
                      {!periodAlreadyHasReading && isDateOutsideDueWindow && dueWindowRange && (
                        <View style={styles.periodWarning}>
                          <InfoIcon size={14} color="#EF4444" />
                          <Text style={styles.periodWarningText}>
                            يجب أن يكون تاريخ القراءة ضمن فترة الاستحقاق ({formatShortDate(dueWindowRange.start)} - {formatShortDate(dueWindowRange.end)}).
                          </Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>
              </View>
              )}

              {/* Date input for edit mode */}
              {isEditMode && (
                <View style={[styles.periodsSection, { backgroundColor: themeColors.section }]}>
                  <View style={styles.dateInputWrapper}>
                    <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('kpis.measurementDate')}</Text>
                    <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                      <CalendarIcon size={18} color={themeColors.iconColor} />
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={formData.measurementDate}
                          onChange={(e: any) => updateField('measurementDate', e.target.value)}
                          style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            backgroundColor: 'transparent',
                            fontSize: 14,
                            fontFamily: FONTS.regular,
                            color: isDark ? '#E5E7EB' : '#414042',
                            direction: 'ltr',
                          } as any}
                        />
                      ) : (
                        <TextInput
                          style={[styles.dateInput, { color: themeColors.inputText }]}
                          value={formData.measurementDate}
                          onChangeText={(text) => updateField('measurementDate', text)}
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor={themeColors.placeholder}
                        />
                      )}
                    </View>
                  </View>
                </View>
              )}

              {/* Value Inputs Row */}
              <View style={[styles.valueRow, isSmallScreen && styles.valueRowSmall]}>
                {/* Target Value - Left side, disabled */}
                <View style={styles.valueBox}>
                  <Text style={[styles.valueBoxLabel, { color: themeColors.label }]}>{t('kpis.targetValue')}</Text>
                  <View style={[styles.valueDisplayContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.valueUnitText, { color: themeColors.iconColor }]}>{translate(kpi?.unit) || t('kpis.number')}</Text>
                    <Text style={[styles.valueDisplayText, { color: themeColors.inputText }]}>{kpi?.target_value || '--'}</Text>
                  </View>
                </View>

                {/* Achieved Value - Right side, active */}
                <View style={styles.valueBox}>
                  <View style={styles.labelRowRight}>
                    <Text style={styles.requiredStar}>*</Text>
                    <Text style={[styles.valueBoxLabel, { color: themeColors.label }]}>{t('readings.achievedValue')}</Text>
                  </View>
                  <View style={[styles.valueInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }, errors.value && styles.inputError]}>
                    <Text style={[styles.valueUnitText, { color: themeColors.iconColor }]}>{translate(kpi?.unit) || t('kpis.number')}</Text>
                    <TextInput
                      style={[styles.valueInput, { color: themeColors.inputText }]}
                      value={formData.value}
                      onChangeText={(v) => updateField('value', normalizeArabicNumbers(v))}
                      keyboardType="numeric"
                      textAlign="right"
                      placeholder="00"
                      placeholderTextColor={themeColors.placeholder}
                    />
                  </View>
                  {errors.value && <Text style={styles.errorText}>{errors.value}</Text>}
                </View>
              </View>

              {/* Info Message */}
              <View style={[styles.infoMessage, { backgroundColor: themeColors.infoMessage }]}>
                <Text style={[styles.infoText, { color: themeColors.inputText }]}>{t('readings.achievementCalculationInfo')}</Text>
                <InfoIcon size={20} color={themeColors.inputText} />
              </View>

              {/* Evidence Section - Gray Background */}
              <View style={[styles.evidenceSection, { backgroundColor: themeColors.section }, errors.evidence && { borderColor: '#EF4444', borderWidth: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.sectionLabel, { color: themeColors.sectionTitle }]}>{t('readings.addEvidence')}</Text>
                </View>

                {/* File Upload Option */}
                <TouchableOpacity
                  style={styles.evidenceRadioRow}
                  onPress={() => { setEvidenceType('file'); if (errors.evidence) setErrors(prev => ({ ...prev, evidence: '' })); }}
                >
                  <Text style={[styles.evidenceRadioLabel, { color: themeColors.sectionTitle }]}>{t('readings.attachEvidenceFile')}</Text>
                  <View style={[styles.radio, { borderColor: themeColors.radioBorder }, evidenceType === 'file' && styles.radioActive]}>
                    {evidenceType === 'file' && <View style={styles.radioInner} />}
                  </View>
                </TouchableOpacity>

                {evidenceType === 'file' && (
                  <View style={styles.uploadSection}>
                    <View style={styles.fileInputRow}>
                      <TouchableOpacity style={[styles.uploadButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]} onPress={handleFilePick}>
                        <Text style={[styles.uploadButtonText, { color: themeColors.sectionTitle }]}>{t('readings.selectFile')}</Text>
                      </TouchableOpacity>
                      <View style={[styles.fileNameContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                        <Text style={[styles.fileNameText, { color: themeColors.iconColor }]}>{selectedFile?.name || t('readings.noFileSelected')}</Text>
                      </View>
                    </View>
                    <View style={styles.uploadInfo}>
                      <Text style={styles.uploadInfoText}>{t('readings.allowedFileTypes')}</Text>
                      <InfoIcon size={14} color={themeColors.placeholder} />
                    </View>
                  </View>
                )}

                {/* Text Input Option */}
                <TouchableOpacity
                  style={styles.evidenceRadioRow}
                  onPress={() => { setEvidenceType('text'); if (errors.evidence) setErrors(prev => ({ ...prev, evidence: '' })); }}
                >
                  <Text style={[styles.evidenceRadioLabel, { color: themeColors.sectionTitle }]}>{t('readings.enterText')}</Text>
                  <View style={[styles.radio, { borderColor: themeColors.radioBorder }, evidenceType === 'text' && styles.radioActive]}>
                    {evidenceType === 'text' && <View style={styles.radioInner} />}
                  </View>
                </TouchableOpacity>

                {evidenceType === 'text' && (
                  <View style={styles.evidenceTextSection}>
                    <TextInput
                      style={[styles.evidenceTextInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                      value={formData.evidenceText}
                      onChangeText={(v) => updateField('evidenceText', v)}
                      placeholder=""
                      placeholderTextColor={themeColors.placeholder}
                      textAlign="right"
                      multiline
                      numberOfLines={3}
                    />
                    <View style={styles.uploadInfo}>
                      <Text style={styles.uploadInfoText}>{t('readings.textEvidenceInfo')}</Text>
                      <InfoIcon size={14} color={themeColors.placeholder} />
                    </View>
                  </View>
                )}
                {errors.evidence && <Text style={styles.errorText}>{errors.evidence}</Text>}
              </View>

              {/* Notes Field */}
              <View style={styles.notesField}>
                <View style={styles.labelRow}>
                  {isLateReading && <Text style={styles.requiredStar}>*</Text>}
                  <Text style={[styles.sectionLabel, { color: themeColors.sectionTitle }]}>{t('common.notes')}</Text>
                </View>
                {Platform.OS === 'web' ? (
                  <textarea
                    value={formData.notes}
                    onChange={(e: any) => updateField('notes', e.target.value)}
                    style={{
                      backgroundColor: isDark ? colors.card : '#FFFFFF',
                      borderWidth: 1,
                      borderColor: errors.notes ? '#EF4444' : (isDark ? colors.border : '#E5E7EB'),
                      borderStyle: 'solid',
                      borderRadius: 8,
                      padding: 12,
                      minHeight: 80,
                      fontSize: 14,
                      fontFamily: FONTS.regular,
                      color: isDark ? '#E5E7EB' : '#414042',
                      textAlign: 'right' as const,
                      direction: 'rtl' as const,
                      resize: 'vertical' as const,
                      outline: 'none',
                    }}
                  />
                ) : (
                  <TextInput
                    style={[styles.notesInput, { backgroundColor: themeColors.input, borderColor: errors.notes ? '#EF4444' : themeColors.inputBorder, color: themeColors.inputText }]}
                    value={formData.notes}
                    onChangeText={(v) => updateField('notes', v)}
                    placeholder=""
                    placeholderTextColor={themeColors.placeholder}
                    textAlign="right"
                    multiline
                    numberOfLines={3}
                  />
                )}
                {errors.notes && <Text style={styles.errorText}>{errors.notes}</Text>}
                <View style={styles.notesInfo}>
                  <Text style={styles.notesInfoText}>
                    {isLateReading ? t('readings.lateReadingNotesRequired') : t('readings.notesOptional')}
                  </Text>
                  <InfoIcon size={14} color={themeColors.placeholder} />
                </View>
              </View>

              {/* Action Buttons */}
              <View style={styles.actionButtons}>
                <TouchableOpacity
                  style={[styles.saveButton, (submitting || periodAlreadyHasReading) && styles.saveButtonDisabled]}
                  onPress={handleSave}
                  disabled={submitting || periodAlreadyHasReading}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.saveButtonText}>{t('common.save')}</Text>
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
                  onPress={handleCancel}
                  disabled={submitting}
                >
                  <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* Right Column - KPI Info */}
          <View style={[styles.rightColumn, isSmallScreen && styles.rightColumnSmall]}>
            {/* KPI Information Card */}
            <View style={[styles.infoCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.infoCardTitle, { color: themeColors.inputText }]}>{t('readings.kpiInformation')}</Text>

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{t('kpis.strategic')}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.type')}</Text>
                </View>
              </View>

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{kpi?.target_value || '--'} {translate(kpi?.unit) || t('kpis.number')}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.target')}</Text>
                </View>
              </View>

              {kpi?.worst_acceptable_value != null && (
                <View style={styles.infoRow}>
                  <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{kpi.worst_acceptable_value} {translate(kpi?.unit) || t('kpis.number')}</Text>
                  <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                    <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.worstAcceptableValue')}</Text>
                  </View>
                </View>
              )}

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{kpi?.polarity ? getPolarityText(kpi.polarity) : t('readings.polarityPositive')}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.polarity')}</Text>
                </View>
              </View>

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{kpi?.measurement_formula || '—'}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.measurementFormula')}</Text>
                </View>
              </View>

              <View style={[styles.infoRow, styles.infoRowLast]}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{translate(kpi?.owner_name) || '—'}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.responsible')}</Text>
                </View>
              </View>
            </View>

            {/* Current Year Progress Card */}
            <View style={[styles.progressCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.progressCardTitle, { color: themeColors.inputText }]}>{t('readings.currentYearProgress')}</Text>
              <View style={styles.progressContent}>
                <View style={styles.progressTextContainer}>
                  <View style={styles.progressRow}>
                    <Text style={[styles.progressValue, { color: themeColors.inputText }]}>{currentYearReadingsCount}/{getExpectedReadingsPerYear(kpi?.frequency)}</Text>
                    <View style={[styles.progressLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                      <Text style={[styles.progressLabelText, { color: themeColors.inputText }]}>{t('readings.readings')}</Text>
                    </View>
                  </View>
                  <View style={styles.progressRow}>
                    <Text style={[styles.progressValue, { color: themeColors.inputText }]}>{kpi?.achievement_percentage ?? 0} %</Text>
                    <View style={[styles.progressLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                      <Text style={[styles.progressLabelText, { color: themeColors.inputText }]}>{t('readings.percentage')}</Text>
                    </View>
                  </View>
                </View>
                <ProgressCircle
                  percentage={kpi?.achievement_percentage ?? 0}
                  readings={currentYearReadingsCount}
                  total={getExpectedReadingsPerYear(kpi?.frequency)}
                />
              </View>
            </View>

            {/* Recent Readings Card */}
            <View style={[styles.readingsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.readingsTitle, { color: themeColors.pageTitle }]}>{t('readings.recentReadings')}</Text>
              {recentReadings.length > 0 ? (
                recentReadings.map((reading, index) => (
                  <View key={reading.id || index} style={[styles.readingItem, { backgroundColor: themeColors.readingBg }]}>
                    <View style={[styles.readingColorBar, { backgroundColor: getReadingColor(index) }]} />
                    <View style={styles.readingContent}>
                      <Text style={[styles.readingValue, { color: themeColors.pageTitle }]}>{reading.value} {translate(kpi?.unit) || t('kpis.number')}</Text>
                      <Text style={[styles.readingDate, { color: themeColors.iconColor }]}>{formatShortDate(reading.measurement_date)}</Text>
                    </View>
                  </View>
                ))
              ) : (
                <View style={{ alignItems: 'center', paddingVertical: 16 }}>
                  <Text style={{ color: themeColors.placeholder, fontSize: 14 }}>{t('readings.noPreviousReadings')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  contentSmall: {
    padding: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: 4,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Page Title Row
  pageTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  pageTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#1F2937',
    textAlign: 'right',
    flex: 1,
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  viewAllText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  // Main Content
  mainContent: {
    flexDirection: 'row',
    gap: 24,
  },
  mainContentSmall: {
    flexDirection: 'column-reverse',
    gap: 16,
  },
  leftColumn: {
    flex: 2,
  },
  leftColumnSmall: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
    gap: 12,
  },
  rightColumnSmall: {
    flex: 1,
  },
  // Form Card
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  formTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#1F2937',
    textAlign: 'right',
    marginBottom: 20,
  },
  // Periods Section
  periodsSection: {
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  periodGroup: {
    marginBottom: 16,
  },
  periodGroupTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#374151',
    textAlign: 'right',
    marginBottom: 8,
  },
  periodRadioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    minHeight: 44,
    paddingVertical: 4,
  },
  periodLabelContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  periodText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  periodTextActive: {
    color: '#A9211F',
  },
  dueDateText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    marginTop: 2,
  },
  // Radio
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioActive: {
    borderColor: '#1F2937',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#1F2937',
  },
  // Date Input
  dateInputWrapper: {
    marginTop: 12,
  },
  periodWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    paddingHorizontal: 4,
  },
  periodWarningText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    flex: 1,
  },
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 12,
    gap: 8,
  },
  dateInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  // Value Row
  valueRow: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 8,
  },
  valueRowSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  valueBox: {
    flex: 1,
  },
  valueBoxLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 6,
  },
  labelRowRight: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 0,
    marginBottom: 6,
  },
  valueDisplayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
    opacity: 0.5,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  valueDisplayText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  valueUnitText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  valueInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    minHeight: 44,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  valueInput: {
    flex: 1,
    paddingVertical: 7.5,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  // Labels
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#374151',
  },
  requiredStar: {
    fontSize: 14,
    color: '#D62728',
  },
  // Info Message
  infoMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginBottom: 20,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Evidence Section
  evidenceSection: {
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  evidenceRadioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 12,
    minHeight: 44,
  },
  evidenceRadioLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#374151',
  },
  uploadSection: {
    marginBottom: 16,
    paddingRight: 28,
  },
  fileInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  fileNameContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: 'center',
  },
  fileNameText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
  },
  uploadButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: 'center',
  },
  uploadButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#374151',
  },
  uploadInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
  },
  uploadInfoText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
  },
  evidenceTextSection: {
    paddingRight: 28,
    marginBottom: 12,
  },
  evidenceTextInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
    minHeight: 80,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    textAlignVertical: 'top',
    marginBottom: 8,
  },
  // Notes Field
  notesField: {
    marginBottom: 24,
  },
  notesInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
    minHeight: 80,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    textAlignVertical: 'top',
    marginBottom: 8,
  },
  notesInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
  },
  notesInfoText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 32,
    paddingVertical: 12,
    minWidth: 100,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#D1D5DB',
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 32,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#374151',
  },
  // Error
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },
  // Info Card
  infoCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  infoCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  infoRowLast: {
    marginBottom: 0,
  },
  infoValue: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  infoLabelBadge: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    width: 80,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  infoLabelText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Progress Card
  progressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  progressCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },
  progressContent: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
  progressTextContainer: {
    flex: 1,
    gap: 8,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  progressLabelBadge: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    height: 20,
    width: 80,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  progressLabelText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  progressValue: {
    fontSize: 20,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
    textAlign: 'center',
  },
  // Readings Card
  readingsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  readingsTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#1F2937',
    textAlign: 'center',
    marginBottom: 16,
  },
  readingItem: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginBottom: 8,
    overflow: 'hidden',
  },
  readingColorBar: {
    width: 4,
  },
  readingContent: {
    flex: 1,
    padding: 12,
    alignItems: 'flex-end',
  },
  readingValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#1F2937',
  },
  readingDate: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginTop: 2,
  },
  noReadingsText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'center',
    paddingVertical: 20,
  },
});
